import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Dialog } from "@mui/material";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import PlayCircleRoundedIcon from "@mui/icons-material/PlayCircleRounded";
import { selectUser } from "../../features/userSlice";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { EmptyState, LoadingState, Spinner } from "../../components/UI/States";
import { BackLink, BranchSelect, Notice } from "../../components/Fleet/FleetParts";
import { formatDate } from "../../components/Fleet/fleetFormat";
import { fetchToolboxResults, fetchToolboxTalkResults } from "./safetyApi";
import { Chip, DialogHeader, UploadVideoDialog, pickBranch, plural, sameBranch, useUrlFilters } from "./SafetyParts";
import { SAFETY_PAGES } from "./safetyPages";
import { ReportActions, reportScope } from "./SafetyReport";

// Toolbox talk results: who has finished each talk (passed its quiz, or
// marked present when it has no quiz), plus uploading new talks. Everyone
// watches at /safety/toolbox-talks or in the app; quizzes are built in the
// app (Safety Manager → Toolbox Talks).

const PAGE = SAFETY_PAGES.toolbox;

// Per-branch count maps from the server → one number, optionally for a branch.
const total = (byBranch, branch) =>
  Object.entries(byBranch || {})
    .filter(([b]) => !branch || sameBranch(b, branch))
    .reduce((sum, [, n]) => sum + n, 0);

const PersonList = ({ title, people, render, empty }) => (
  <section className="mt-5">
    <p className="app-eyebrow mb-2">
      {title} · {people.length}
    </p>
    {people.length === 0 ? (
      <p className="app-muted text-sm">{empty}</p>
    ) : (
      <ul className="max-h-64 divide-y divide-[color:var(--app-divider)] overflow-y-auto rounded-xl border app-divider">
        {people.map((p) => (
          <li key={p.email} className="flex items-start justify-between gap-3 px-3 py-2 text-sm">
            <span className="min-w-0">
              <span className="block truncate font-semibold">{p.name}</span>
              <span className="app-muted block text-xs">{p.branch}</span>
            </span>
            <span className="shrink-0 text-right text-xs">{render(p)}</span>
          </li>
        ))}
      </ul>
    )}
  </section>
);

const TalkDialog = ({ talk, branch, userId, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setDetail(null);
    setError(null);
    if (!talk) return undefined;
    let active = true;
    fetchToolboxTalkResults(userId, talk.id)
      .then((d) => active && setDetail(d))
      .catch((e) => active && setError(e.message));
    return () => {
      active = false;
    };
  }, [talk, userId]);

  const here = (list) => (list || []).filter((p) => !branch || sameBranch(p.branch, branch));

  return (
    <Dialog open={Boolean(talk)} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { m: 2 } }} aria-labelledby="talk-title">
      {talk && (
        <div className="p-5 sm:p-6">
          <DialogHeader
            id="talk-title"
            icon={PAGE.icon}
            tone={PAGE.tone}
            title={talk.title}
            subtitle={`Posted ${formatDate(talk.createdAt)}${branch ? ` · ${branch} only` : ""}`}
            onClose={onClose}
          />
          {error && (
            <div className="mt-4">
              <Notice tone="rose">{error}</Notice>
            </div>
          )}
          {!detail && !error && (
            <div className="mt-4 flex items-center gap-2 text-sm">
              <Spinner size={18} /> <span className="app-muted">Loading who's done…</span>
            </div>
          )}
          {detail && (
            <>
              <PersonList
                title="Done"
                people={here(detail.done)}
                empty="Nobody has finished it yet."
                render={(p) => (
                  <>
                    <span className="block">{formatDate(p.at)}</span>
                    <span className="app-muted">
                      {p.completionType === "quiz"
                        ? `Quiz${p.quizPct !== null ? ` ${p.quizPct}%` : ""}`
                        : "Marked present"}
                    </span>
                  </>
                )}
              />
              {talk.quizEnabled && (
                <PersonList
                  title="Didn't pass the quiz"
                  people={here(detail.failedQuiz)}
                  empty="Nobody is stuck on the quiz."
                  render={(p) => <span className="text-rose-700 dark:text-rose-300">{p.pct !== null ? `${p.pct}%` : "Failed"}</span>}
                />
              )}
              <PersonList
                title="Started watching, not finished"
                people={here(detail.unfinished)}
                empty="Nobody has a half-watched talk."
                render={(p) => (
                  <span className="app-muted">{p.watchedPct !== null ? `Watched ${p.watchedPct}%` : formatDate(p.at)}</span>
                )}
              />
              <div className="mt-5 flex justify-end border-t app-divider pt-4">
                <ReportActions
                  kind="toolbox-talk"
                  userId={userId}
                  tone={PAGE.tone}
                  selection={{ talkId: talk.id, branch, scope: reportScope({ branch }) }}
                />
              </div>
            </>
          )}
        </div>
      )}
    </Dialog>
  );
};

const ToolboxResults = () => {
  const user = useSelector(selectUser);
  const filters = useUrlFilters();
  const [talks, setTalks] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    if (!user?.id) return;
    fetchToolboxResults(user.id)
      .then((data) => setTalks(data.talks))
      .catch((e) => setError(e.message));
  }, [user?.id]);

  useEffect(() => load(), [load]);

  const branches = useMemo(
    () =>
      [
        ...new Set(
          (talks || []).flatMap((t) => [...Object.keys(t.done), ...Object.keys(t.unfinished), ...Object.keys(t.quizTaken)])
        ),
      ].sort((a, b) => a.localeCompare(b)),
    [talks]
  );
  const branch = pickBranch(branches, filters.branch);

  return (
    <PageShell size="lg">
      <UploadVideoDialog kind={uploading} userId={user?.id} onClose={() => setUploading(null)} onUploaded={load} />
      <TalkDialog talk={open} branch={branch} userId={user?.id} onClose={() => setOpen(null)} />
      <BackLink to="/safety/overview">Safety Overview</BackLink>
      <PageHeader
        icon={PAGE.icon}
        tone={PAGE.tone}
        eyebrow="Safety"
        title={PAGE.name}
        description="Who has finished each toolbox talk. Done means they passed the talk's quiz, or marked themselves present if it has no quiz, in the Big C mobile app."
        actions={
          <>
            <Link to="/safety/toolbox-talks" className="app-btn app-btn-secondary">
              <PlayCircleRoundedIcon sx={{ fontSize: 18 }} /> Watch the talks
            </Link>
            <button type="button" className="app-btn app-btn-primary" onClick={() => setUploading("toolbox")}>
              <CloudUploadRoundedIcon sx={{ fontSize: 18 }} /> Upload toolbox talk
            </button>
          </>
        }
      />

      {error && <Notice tone="rose">{error}</Notice>}
      {!talks && !error && <LoadingState label="Loading toolbox talks…" />}

      {talks && (
        <>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <BranchSelect value={branch} onChange={(v) => filters.set("branch", v)} branches={branches} />
            <ReportActions
              kind="toolbox"
              userId={user?.id}
              tone={PAGE.tone}
              className="sm:ml-auto"
              disabled={!talks.length}
              selection={{ branch, ids: talks.map((t) => t.id), scope: reportScope({ branch }) }}
            />
          </div>

          <div className="app-card overflow-hidden rounded-2xl border">
            {talks.length === 0 ? (
              <EmptyState compact icon={PAGE.icon} title="No toolbox talks yet." description="Upload one to get started." />
            ) : (
              <ul className="divide-y divide-[color:var(--app-divider)]">
                {talks.map((talk, i) => {
                  const done = total(talk.done, branch);
                  const taken = total(talk.quizTaken, branch);
                  const passed = total(talk.quizPassed, branch);
                  const unfinished = total(talk.unfinished, branch);
                  return (
                    <li key={talk.id}>
                      <button
                        type="button"
                        onClick={() => setOpen(talk)}
                        className="flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-[color:var(--app-hover)] sm:px-5"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold">{talk.title}</span>
                            {i === 0 && <Chip tone="sky">Latest</Chip>}
                          </span>
                          <span className="app-muted block text-xs">
                            Posted {formatDate(talk.createdAt)}
                            {talk.addedByName ? ` by ${talk.addedByName}` : ""}
                            {talk.quizEnabled
                              ? taken
                                ? ` · ${passed} of ${taken} passed the quiz`
                                : " · quiz not taken yet"
                              : " · no quiz"}
                          </span>
                          {unfinished > 0 && (
                            <span className="block text-xs text-amber-700 dark:text-amber-300">
                              {plural(unfinished, "person", "people")} started watching but didn't finish
                            </span>
                          )}
                        </span>
                        <span className="shrink-0 text-right">
                          <span className="block text-xl font-bold tabular-nums">{done}</span>
                          <span className="app-muted text-xs">done</span>
                        </span>
                        <ChevronRightRoundedIcon className="app-muted mt-1 shrink-0" fontSize="small" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <p className="app-muted mt-3 text-xs">
            To add or change a talk's quiz, use Safety Manager → Toolbox Talks in the Big C mobile app.
          </p>
        </>
      )}
    </PageShell>
  );
};

export default ToolboxResults;
